import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Ticket, MessageSquare, User as UserIcon, Clock, ArrowRight, Send, CheckCircle2, XCircle, Trash2 } from "lucide-react";

const STATUS_META = {
  open: { label: "Ouvert", bg: "bg-sky-50 text-sky-700 border-sky-200" },
  in_progress: { label: "En cours", bg: "bg-amber-50 text-amber-700 border-amber-200" },
  resolved: { label: "Résolu", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed: { label: "Clos", bg: "bg-gray-50 text-gray-600 border-gray-200" },
};
const PRIORITY_META = {
  low: { label: "Faible", color: "#059669" },
  medium: { label: "Moyenne", color: "#D97706" },
  high: { label: "Haute", color: "#E60000" },
  urgent: { label: "Urgente", color: "#7C2D12" },
};

const empty = () => ({ subject: "", description: "", category: "general", priority: "medium", assignee_id: "" });

export default function ChefQualiParcTickets() {
  const [items, setItems] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [filter, setFilter] = useState({ status: "all", priority: "all" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/chef/qualiparc/tickets"),
        api.get("/chef/salaries"),
      ]);
      setItems(a.data || []);
      setSalaries(b.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((t) => (filter.status === "all" || t.status === filter.status) && (filter.priority === "all" || t.priority === filter.priority)), [items, filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { subject: form.subject.trim(), description: form.description, category: form.category, priority: form.priority, assignee_id: form.assignee_id || null };
      await api.post("/chef/qualiparc/tickets", payload);
      toast.success("Ticket créé");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/chef/qualiparc/tickets/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      if (detail?.id === id) setDetail(data);
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce ticket ?")) return;
    try { await api.delete(`/chef/qualiparc/tickets/${id}`); load(); if (detail?.id === id) setDetail(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !detail) return;
    try {
      await api.post(`/chef/qualiparc/tickets/${detail.id}/comments`, { message: reply.trim() });
      setReply("");
      const { data } = await api.get(`/chef/qualiparc/tickets/${detail.id}`);
      setDetail(data);
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="QualiParc"
        title="Tickets"
        sub="Ticketing chef ↔ salariés : incidents, demandes, questions internes."
        right={
          <Button onClick={() => setOpen(true)} data-testid="qp-new-ticket-btn" className="h-10 bg-[#002FA7] hover:bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] font-bold rounded-none">
            <Plus className="h-4 w-4 mr-2" /> Nouveau ticket
          </Button>
        }
      />

      <div className="p-6 lg:p-10">
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div>
            <Label className="text-xs">Statut</Label>
            <Select value={filter.status} onValueChange={(v) => setFilter((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="w-40 h-9" data-testid="qp-tickets-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Priorité</Label>
            <Select value={filter.priority} onValueChange={(v) => setFilter((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="border border-[#DEE2E6] bg-white p-10 text-center text-sm text-[#868E96]" data-testid="qp-tickets-empty">
            <Ticket className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Aucun ticket.
          </div>
        ) : (
          <div className="border border-[#DEE2E6] bg-white divide-y divide-[#F1F3F5]">
            {filtered.map((t) => {
              const st = STATUS_META[t.status] || STATUS_META.open;
              const pr = PRIORITY_META[t.priority] || PRIORITY_META.medium;
              return (
                <button
                  type="button"
                  key={t.id}
                  data-testid={`qp-ticket-row-${t.id}`}
                  onClick={() => setDetail(t)}
                  className="w-full text-left p-4 hover:bg-[#F8F9FA] transition-colors flex items-center justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border font-bold ${st.bg}`}>{st.label}</span>
                      <span className="text-[10px] uppercase font-bold tracking-[0.15em]" style={{ color: pr.color }}>● {pr.label}</span>
                      <span className="text-[10px] uppercase text-[#868E96] tracking-[0.15em]">{t.category}</span>
                    </div>
                    <div className="mt-1 font-display font-bold truncate">{t.subject}</div>
                    <div className="mt-1 text-xs text-[#868E96] flex items-center gap-3 flex-wrap">
                      <span><UserIcon className="h-3 w-3 inline mr-1" /> {t.opened_by_name}</span>
                      {t.assignee_name && <span>→ {t.assignee_name}</span>}
                      <span><Clock className="h-3 w-3 inline mr-1" /> {new Date(t.updated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                      <span><MessageSquare className="h-3 w-3 inline mr-1" /> {(t.comments || []).length}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#868E96] flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau ticket</DialogTitle>
            <DialogDescription>Créez un ticket que vous pouvez assigner à un salarié.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Sujet *</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="qp-ticket-form-subject" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Catégorie</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Assigner à</Label>
              <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#002FA7]" data-testid="qp-ticket-form-submit">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <Ticket className="h-4 w-4" /> {detail.subject}
                </DialogTitle>
                <DialogDescription>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border font-bold ${STATUS_META[detail.status]?.bg}`}>{STATUS_META[detail.status]?.label}</span>
                  <span className="text-[10px] uppercase ml-2 font-bold" style={{ color: PRIORITY_META[detail.priority]?.color }}>{PRIORITY_META[detail.priority]?.label}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {detail.description && <div className="p-3 border border-[#DEE2E6] bg-[#F8F9FA]">{detail.description}</div>}
                <div className="text-xs text-[#868E96]">Ouvert par <b>{detail.opened_by_name}</b> — {detail.assignee_name ? `Assigné à ${detail.assignee_name}` : "Non assigné"}</div>

                <div className="border-t border-[#F1F3F5] pt-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-2">Commentaires ({(detail.comments || []).length})</div>
                  <div className="space-y-2">
                    {(detail.comments || []).map((c) => (
                      <div key={c.id} className={`p-2 border ${c.author_role === "chef" ? "border-[#002FA7]/30 bg-[#002FA7]/5" : "border-[#DEE2E6] bg-white"}`}>
                        <div className="flex items-center justify-between text-[10px] text-[#868E96] mb-1">
                          <span className="font-semibold">{c.author_name}</span>
                          <span>{new Date(c.created_at).toLocaleString("fr-FR")}</span>
                        </div>
                        <div className="text-xs">{c.message}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Input placeholder="Ajouter un commentaire…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} data-testid="qp-ticket-reply-input" />
                    <Button onClick={sendReply} className="bg-[#002FA7]" data-testid="qp-ticket-reply-send"><Send className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 border-t border-[#F1F3F5] pt-3">
                  {detail.status !== "in_progress" && detail.status !== "resolved" && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(detail.id, "in_progress")} data-testid="qp-ticket-progress">En cours</Button>
                  )}
                  {detail.status !== "resolved" && detail.status !== "closed" && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(detail.id, "resolved")} data-testid="qp-ticket-resolve"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Résoudre</Button>
                  )}
                  {detail.status !== "closed" && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(detail.id, "closed")}><XCircle className="h-3.5 w-3.5 mr-1" /> Clore</Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(detail.id)}><Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
